/**
 * Comprehensive API Error Handling Utility
 * Provides consistent error handling, retry logic, and timeout management
 */

import { toast } from "react-hot-toast";

// Error types
export const ErrorTypes = {
  NETWORK: "NETWORK_ERROR",
  TIMEOUT: "TIMEOUT_ERROR",
  AUTHENTICATION: "AUTH_ERROR",
  VALIDATION: "VALIDATION_ERROR",
  SERVER: "SERVER_ERROR",
  RATE_LIMIT: "RATE_LIMIT_ERROR",
  NOT_FOUND: "NOT_FOUND_ERROR",
  PERMISSION: "PERMISSION_ERROR",
  UNKNOWN: "UNKNOWN_ERROR",
};

// Retry configuration
const RETRY_CONFIG = {
  maxRetries: 3,
  retryDelay: 1000, // 1 second
  backoffMultiplier: 2,
  retryableErrors: [
    ErrorTypes.NETWORK,
    ErrorTypes.TIMEOUT,
    ErrorTypes.RATE_LIMIT,
    "ECONNABORTED",
    "ETIMEDOUT",
  ],
};

// Default timeout
const DEFAULT_TIMEOUT = 30000; // 30 seconds

/**
 * Determine error type from error object
 */
function getErrorType(error) {
  if (!error.response) {
    // Network error or timeout
    if (
      error.code === "ECONNABORTED" ||
      error.code === "TIMEOUT" ||
      error.message.includes("timeout") ||
      error.name === "AbortError"
    ) {
      return ErrorTypes.TIMEOUT;
    }
    return ErrorTypes.NETWORK;
  }

  const status = error.response.status;

  switch (status) {
    case 401:
      return ErrorTypes.AUTHENTICATION;
    case 403:
      return ErrorTypes.PERMISSION;
    case 404:
      return ErrorTypes.NOT_FOUND;
    case 422:
    case 400:
      return ErrorTypes.VALIDATION;
    case 429:
      return ErrorTypes.RATE_LIMIT;
    case 500:
    case 502:
    case 503:
    case 504:
      return ErrorTypes.SERVER;
    default:
      return ErrorTypes.UNKNOWN;
  }
}

/**
 * Get user-friendly error message
 */
function getUserMessage(errorType, error) {
  const defaultMessages = {
    [ErrorTypes.NETWORK]:
      "Unable to connect to the server. Please check your internet connection.",
    [ErrorTypes.TIMEOUT]: "The request took too long. Please try again.",
    [ErrorTypes.AUTHENTICATION]:
      "Your session has expired. Please log in again.",
    [ErrorTypes.VALIDATION]: "Please check your input and try again.",
    [ErrorTypes.SERVER]: "The server could not complete that request.",
    [ErrorTypes.RATE_LIMIT]:
      "Too many requests. Please wait a moment and try again.",
    [ErrorTypes.NOT_FOUND]: "The requested resource was not found.",
    [ErrorTypes.PERMISSION]:
      "You do not have permission to perform this action.",
    [ErrorTypes.UNKNOWN]: "An unexpected error occurred. Please try again.",
  };

  // Check for custom error message from server
  if (error.response?.data?.message) {
    return error.response.data.message;
  }

  return defaultMessages[errorType] || defaultMessages[ErrorTypes.UNKNOWN];
}

/**
 * Log error for debugging
 */
function logError(error, context = {}) {
  const errorInfo = {
    timestamp: new Date().toISOString(),
    type: getErrorType(error),
    message: error.message,
    stack: error.stack,
    context,
    response: error.response?.data,
    status: error.response?.status,
    url: error.config?.url,
    method: error.config?.method,
  };

  // In development, log to console
  if (import.meta.env.MODE === "development") {
  }

  // In production, send to error tracking service
  if (import.meta.env.MODE === "production" && window.errorTracker) {
    window.errorTracker.logError(errorInfo);
  }
}

const PUBLIC_PATHS = new Set([
  "/",
  "/login",
  "/register",
  "/reset-password",
  "/auth/oauth/callback",
]);

/**
 * Handle authentication errors
 */
async function handleAuthError(error) {

  // Never redirect guests away from public routes — the landing page,
  // login, and registration surfaces must render for unauthenticated users.
  if (PUBLIC_PATHS.has(window.location.pathname)) return;

  window.location.href = "/login?expired=true";
}

/**
 * Sleep function for retry delays
 */
function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Retry logic with exponential backoff
 */
async function retryRequest(fn, options = {}) {
  const config = { ...RETRY_CONFIG, ...options };
  let lastError;

  for (let attempt = 0; attempt <= config.maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      const errorType = getErrorType(error);

      // Don't retry non-retryable errors
      if (!config.retryableErrors.includes(errorType)) {
        throw error;
      }

      // Don't retry if this was the last attempt
      if (attempt === config.maxRetries) {
        throw error;
      }

      // Calculate delay with exponential backoff
      const delay =
        config.retryDelay * Math.pow(config.backoffMultiplier, attempt);

      // For rate limit errors, check retry-after header
      if (
        errorType === ErrorTypes.RATE_LIMIT &&
        error.response?.headers["retry-after"]
      ) {
        const retryAfter =
          parseInt(error.response.headers["retry-after"]) * 1000;
        await sleep(retryAfter);
      } else {
        await sleep(delay);
      }
    }
  }

  throw lastError;
}

/**
 * Main error handler
 */
export async function handleApiError(error, options = {}) {
  const {
    showToast = true,
    context = {},
    onAuthError = handleAuthError,
    customHandlers = {},
  } = options;

  const errorType = getErrorType(error);
  const errorInfo = {
    type: errorType,
    message: getUserMessage(errorType, error),
    details: error.response?.data,
    status: error.response?.status,
  };

  logError(error, context);

  if (customHandlers[errorType]) {
    await customHandlers[errorType](error);
  } else if (errorType === ErrorTypes.AUTHENTICATION) {
    await onAuthError(error);
  } else if (showToast) {
    toast.error(errorInfo.message);
  }

  return errorInfo;
}

/**
 * Wrapper for API calls with error handling and retry logic
 */
export async function apiCall(fn, options = {}) {
  const {
    timeout = DEFAULT_TIMEOUT,
    retries = RETRY_CONFIG.maxRetries,
    showErrorToast = true,
    context = {},
    controller,
    ...otherOptions
  } = options;

  try {
    // If controller is provided, we don't need to add extra timeout handling
    // as it's already handled in the makeRequest function
    const timeoutFn = controller
      ? fn
      : () => {
          return Promise.race([
            fn(),
            new Promise((_, reject) => {
              const timeoutError = new Error(
                `Request timeout after ${timeout}ms`,
              );
              timeoutError.code = "TIMEOUT";
              timeoutError.timeout = timeout;
              setTimeout(() => reject(timeoutError), timeout);
            }),
          ]);
        };

    // Execute with retry logic
    const result = await retryRequest(timeoutFn, {
      maxRetries: retries,
      ...otherOptions,
    });

    return result;
  } catch (error) {
    // Handle the error
    const errorInfo = await handleApiError(error, {
      showToast: showErrorToast,
      context: {
        ...context,
        timeout: error.timeout || timeout,
      },
      ...otherOptions,
    });

    // Re-throw with additional info
    const enhancedError = new Error(errorInfo.message);
    enhancedError.type = errorInfo.type;
    enhancedError.details = errorInfo.details;
    enhancedError.status = errorInfo.status;
    enhancedError.original = error;

    throw enhancedError;
  }
}

/**
 * Create an API method with built-in error handling
 */
export function createApiMethod(fn, defaultOptions = {}) {
  const method = async (...args) => {
    return apiCall(() => fn(...args), defaultOptions);
  };
  // The same call without the error toast, for a page that shows the failure
  // inline itself (one failure, one message).
  method.quiet = async (...args) =>
    apiCall(() => fn(...args), { ...defaultOptions, showErrorToast: false });
  return method;
}

export default {
  ErrorTypes,
  handleApiError,
  apiCall,
  createApiMethod,
  getErrorType,
  getUserMessage,
  logError,
};
